import { collection, getDocs, query, orderBy } from 'firebase/firestore'
import { db } from '@/config/firebase'
import type { Invoice, PrivateUserInfoType, Vehicle } from '@otw/models'
import type { Lead } from '@otw/models/leads/lead'
import type { ShipmentPlan } from '@otw/models/logistics'
import type { TransporterPackage } from '@otw/models/transporter'
import {
  leadConverter,
  shipmentPlanConverter,
  transporterPackageConverter,
  vehicleConverter,
} from '@otw/models/converters'
import type { QueryClient } from '@tanstack/react-query'

export const SEARCH_CACHE_STALE_MS = 1000 * 60 * 5

export const SEARCH_QUERY_KEYS = {
  inventory: ['master_inventory'] as const,
  leads: ['leads'] as const,
  invoices: ['invoices'] as const,
  clientUsers: ['users', 'clients'] as const,
  packages: ['transporter_packages'] as const,
  shipmentPlans: ['shipment_plans'] as const,
  transportTrucks: ['transport_trucks'] as const,
}

export async function fetchMasterInventory(): Promise<Vehicle[]> {
  const collectionRef = collection(db, 'master_inventory').withConverter(vehicleConverter)
  const snapshot = await getDocs(collectionRef)
  return snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }))
}

export async function fetchLeads(): Promise<Lead[]> {
  const collectionRef = collection(db, 'leads').withConverter(leadConverter)
  const snapshot = await getDocs(collectionRef)
  return snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }))
}

export async function fetchInvoices(): Promise<Invoice[]> {
  const invoicesQuery = query(collection(db, 'invoices'), orderBy('time_created', 'desc'))
  const snapshot = await getDocs(invoicesQuery)
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as Invoice[]
}

export async function fetchClientUsers(): Promise<PrivateUserInfoType[]> {
  const snapshot = await getDocs(collection(db, 'users'))
  return snapshot.docs
    .map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }) as PrivateUserInfoType)
    .filter((user) => user.role === 'client')
}

export async function fetchTransporterPackages(): Promise<TransporterPackage[]> {
  const packagesQuery = query(
    collection(db, 'transporter_packages').withConverter(transporterPackageConverter),
    orderBy('created_at', 'desc'),
  )
  const snapshot = await getDocs(packagesQuery)
  return snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }))
}

export async function fetchShipmentPlans(): Promise<ShipmentPlan[]> {
  const snapshot = await getDocs(
    collection(db, 'shipment_plans').withConverter(shipmentPlanConverter),
  )
  return snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }))
}

export type TransportTruckSearchRow = {
  id: string
  vehicle_id?: string
  vin?: string
}

export async function fetchTransportTrucks(): Promise<TransportTruckSearchRow[]> {
  const snapshot = await getDocs(collection(db, 'transport_trucks'))
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      vehicle_id: data.vehicle_id as string | undefined,
      vin: typeof data.vin === 'string' ? data.vin : undefined,
    }
  })
}

export async function prefetchEntityCaches(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.ensureQueryData({
      queryKey: SEARCH_QUERY_KEYS.inventory,
      queryFn: fetchMasterInventory,
      staleTime: SEARCH_CACHE_STALE_MS,
    }),
    queryClient.ensureQueryData({
      queryKey: SEARCH_QUERY_KEYS.leads,
      queryFn: fetchLeads,
      staleTime: SEARCH_CACHE_STALE_MS,
    }),
    queryClient.ensureQueryData({
      queryKey: SEARCH_QUERY_KEYS.invoices,
      queryFn: fetchInvoices,
      staleTime: SEARCH_CACHE_STALE_MS,
    }),
    queryClient.ensureQueryData({
      queryKey: SEARCH_QUERY_KEYS.clientUsers,
      queryFn: fetchClientUsers,
      staleTime: SEARCH_CACHE_STALE_MS,
    }),
    queryClient.ensureQueryData({
      queryKey: SEARCH_QUERY_KEYS.packages,
      queryFn: fetchTransporterPackages,
      staleTime: SEARCH_CACHE_STALE_MS,
    }),
    queryClient.ensureQueryData({
      queryKey: SEARCH_QUERY_KEYS.shipmentPlans,
      queryFn: fetchShipmentPlans,
      staleTime: SEARCH_CACHE_STALE_MS,
    }),
    queryClient.ensureQueryData({
      queryKey: SEARCH_QUERY_KEYS.transportTrucks,
      queryFn: fetchTransportTrucks,
      staleTime: SEARCH_CACHE_STALE_MS,
    }),
  ])
}
